import { useEffect, useState, useCallback } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import { Plus, Pencil, Trash2, Percent, UserPlus, Tag } from "lucide-react";

const emptyCamp = { name: "", percent: 10, start_date: "", end_date: "", active: true };
const emptyCode = { code: "", type: "campaign", owner_customer_id: "", percent: 10, scope: "first_order", start_date: "", end_date: "", active: true };
const selCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white";

export default function Promotions() {
  const { loadSettings } = useAuth();
  const { confirm } = useConfirm();
  const [camps, setCamps] = useState([]);
  const [codes, setCodes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [ref, setRef] = useState({ referral_enabled: true, parrainage_welcome_percent: 0, referrer_reward_threshold: 5, referrer_reward_label: "", referrer_notify_message: "" });

  const [campOpen, setCampOpen] = useState(false);
  const [campForm, setCampForm] = useState(emptyCamp);
  const [campEdit, setCampEdit] = useState(null);

  const [codeOpen, setCodeOpen] = useState(false);
  const [codeForm, setCodeForm] = useState(emptyCode);
  const [codeEdit, setCodeEdit] = useState(null);

  const [campPage, setCampPage] = useState(1);
  const [codePage, setCodePage] = useState(1);
  const pagedCamps = camps.slice((campPage - 1) * PAGE_SIZE, campPage * PAGE_SIZE);
  const pagedCodes = codes.slice((codePage - 1) * PAGE_SIZE, codePage * PAGE_SIZE);

  const loadCamps = useCallback(() => api.get("/promo-campaigns").then((r) => setCamps(r.data)), []);
  const loadCodes = useCallback(() => api.get("/promo-codes").then((r) => setCodes(r.data)), []);
  const loadRef = useCallback(() => api.get("/settings").then((r) => setRef({
    referral_enabled: r.data.referral_enabled !== false,
    parrainage_welcome_percent: r.data.parrainage_welcome_percent || 0,
    referrer_reward_threshold: r.data.referrer_reward_threshold ?? 5,
    referrer_reward_label: r.data.referrer_reward_label || "",
    referrer_notify_message: r.data.referrer_notify_message || "",
  })), []);
  useEffect(() => { loadCamps(); loadCodes(); loadRef(); api.get("/customers").then((r) => setCustomers(r.data)); }, [loadCamps, loadCodes, loadRef]);

  const today = new Date().toISOString().slice(0, 10);
  const isLive = (c) => c.active && c.start_date <= today && c.end_date >= today;

  const saveCamp = async () => {
    if (!campForm.name || !campForm.start_date || !campForm.end_date) { toast.error("Nom et dates requis"); return; }
    if (campForm.active) {
      const ov = (a, b) => a.start_date <= b.end_date && a.end_date >= b.start_date;
      const conflict = camps.find((c) => c.id !== campEdit && c.active && ov(campForm, c));
      if (conflict && !(await confirm({ title: "Chevauchement de dates", description: `La campagne « ${conflict.name} » (-${conflict.percent}%) chevauche ces dates. Continuer ?`, confirmText: "Continuer" }))) return;
    }
    try {
      const body = { ...campForm, percent: Number(campForm.percent) || 0 };
      if (campEdit) await api.put(`/promo-campaigns/${campEdit}`, body); else await api.post("/promo-campaigns", body);
      toast.success("Campagne enregistrée"); setCampOpen(false); loadCamps();
    } catch (e) { toast.error(apiError(e)); }
  };
  const rmCamp = async (id) => { if (!(await confirm({ title: "Supprimer la campagne", description: "Supprimer cette campagne ?", confirmText: "Supprimer", danger: true }))) return; try { await api.delete(`/promo-campaigns/${id}`); loadCamps(); } catch (e) { toast.error(apiError(e)); } };

  const saveCode = async () => {
    if (!codeForm.code) { toast.error("Code requis"); return; }
    if (codeForm.type === "ambassador" && !codeForm.owner_customer_id) { toast.error("Choisissez le client ambassadeur"); return; }
    try {
      const body = { ...codeForm, percent: Number(codeForm.percent) || 0 };
      if (codeEdit) await api.put(`/promo-codes/${codeEdit}`, body); else await api.post("/promo-codes", body);
      toast.success("Code enregistré"); setCodeOpen(false); loadCodes();
    } catch (e) { toast.error(apiError(e)); }
  };
  const rmCode = async (id) => { if (!(await confirm({ title: "Supprimer le code", description: "Supprimer ce code ?", confirmText: "Supprimer", danger: true }))) return; try { await api.delete(`/promo-codes/${id}`); loadCodes(); } catch (e) { toast.error(apiError(e)); } };

  const saveRef = async () => {
    try {
      await api.put("/settings", { ...ref, parrainage_welcome_percent: Number(ref.parrainage_welcome_percent) || 0, referrer_reward_threshold: Number(ref.referrer_reward_threshold) || 0 });
      toast.success("Réglages enregistrés"); loadSettings();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Percent className="text-rose-500" /> Promotions & Ambassadeurs</h1>

      {/* Campagnes par période */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-700 flex items-center gap-2"><Percent size={18} /> Campagnes par période</h2>
          <Button data-testid="add-promo-btn" size="sm" onClick={() => { setCampForm({ ...emptyCamp, start_date: today, end_date: today }); setCampEdit(null); setCampOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus size={16} className="mr-1" /> Campagne</Button>
        </div>
        <p className="text-slate-500 text-sm">Réduction % automatique sur toutes les commandes entre 2 dates.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {pagedCamps.map((c) => (
            <Card key={c.id} data-testid={`promo-row-${c.id}`} className="p-4">
              <div className="flex items-start justify-between">
                <div><h3 className="font-semibold text-slate-800">{c.name}</h3><p className="text-sm text-slate-500">-{c.percent}% · {c.start_date} → {c.end_date}</p></div>
                <span className={`text-xs px-2 py-1 rounded ${isLive(c) ? "bg-emerald-100 text-emerald-700" : c.active ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{isLive(c) ? "En cours" : c.active ? "Programmée" : "Inactive"}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setCampForm({ name: c.name, percent: c.percent, start_date: c.start_date, end_date: c.end_date, active: c.active }); setCampEdit(c.id); setCampOpen(true); }}><Pencil size={14} className="mr-1" /> Modifier</Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => rmCamp(c.id)}><Trash2 size={14} className="mr-1" /> Supprimer</Button>
              </div>
            </Card>
          ))}
          {camps.length === 0 && <p className="text-slate-400 text-sm">Aucune campagne.</p>}
        </div>
        <Pagination page={campPage} total={camps.length} onPage={setCampPage} />
      </section>

      {/* Codes promo & ambassadeurs */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-700 flex items-center gap-2"><Tag size={18} /> Codes promo & Ambassadeurs</h2>
          <Button data-testid="add-code-btn" size="sm" onClick={() => { setCodeForm(emptyCode); setCodeEdit(null); setCodeOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus size={16} className="mr-1" /> Code</Button>
        </div>
        <p className="text-slate-500 text-sm">Codes à partager (Facebook/Instagram) ou liés à un ambassadeur. Saisis à la création de commande, cumulables avec les campagnes.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {pagedCodes.map((c) => (
            <Card key={c.id} data-testid={`code-row-${c.id}`} className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800 font-mono">{c.code} <span className="text-rose-600">-{c.percent}%</span></h3>
                  <p className="text-sm text-slate-500">{c.type === "ambassador" ? `Ambassadeur : ${c.owner_name || "?"}` : "Code campagne"} · {c.scope === "first_order" ? "1ʳᵉ commande" : "toutes commandes"}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Actif" : "Inactif"}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setCodeForm({ code: c.code, type: c.type, owner_customer_id: c.owner_customer_id || "", percent: c.percent, scope: c.scope, start_date: c.start_date || "", end_date: c.end_date || "", active: c.active }); setCodeEdit(c.id); setCodeOpen(true); }}><Pencil size={14} className="mr-1" /> Modifier</Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => rmCode(c.id)}><Trash2 size={14} className="mr-1" /> Supprimer</Button>
              </div>
            </Card>
          ))}
          {codes.length === 0 && <p className="text-slate-400 text-sm">Aucun code.</p>}
        </div>
        <Pagination page={codePage} total={codes.length} onPage={setCodePage} />
      </section>

      {/* Réglages parrainage & ambassadeur */}
      <Card className="p-5 max-w-xl">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><UserPlus size={18} className="text-sky-500" /> Parrainage & récompenses</h3>
        <div className="flex items-center gap-2 mb-3"><Switch data-testid="referral-enabled" checked={ref.referral_enabled} onCheckedChange={(v) => setRef({ ...ref, referral_enabled: v })} /><Label>Activer le parrainage</Label></div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><Label>Réduction de bienvenue filleul (%)</Label><Input data-testid="welcome-percent" type="number" value={ref.parrainage_welcome_percent} onChange={(e) => setRef({ ...ref, parrainage_welcome_percent: e.target.value })} /></div>
          <div><Label>Seuil récompense (nb de filleuls)</Label><Input data-testid="referrer-threshold" type="number" value={ref.referrer_reward_threshold} onChange={(e) => setRef({ ...ref, referrer_reward_threshold: e.target.value })} /></div>
        </div>
        <div className="mb-3"><Label>Récompense parrain/ambassadeur</Label><Input data-testid="referrer-reward-label" value={ref.referrer_reward_label} onChange={(e) => setRef({ ...ref, referrer_reward_label: e.target.value })} placeholder="ex: 10 kg lavage offert" /></div>
        <div className="mb-3"><Label>Message WhatsApp (récompense débloquée)</Label>
          <textarea data-testid="referrer-notify-message" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" rows={3} value={ref.referrer_notify_message} onChange={(e) => setRef({ ...ref, referrer_notify_message: e.target.value })} placeholder="Variables : {name}, {reward}, {company}" />
        </div>
        <p className="text-xs text-slate-400 mb-3">Le parrain/ambassadeur gagne 1 point par 1ʳᵉ commande payée amenée ; au seuil, il débloque la récompense.</p>
        <Button data-testid="save-referral-btn" onClick={saveRef} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button>
      </Card>

      {/* Dialog campagne */}
      <Dialog open={campOpen} onOpenChange={setCampOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{campEdit ? "Modifier la campagne" : "Nouvelle campagne"}</DialogTitle><DialogDescription>Réduction automatique sur la période.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input data-testid="promo-name" value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} /></div>
            <div><Label>Réduction (%)</Label><Input data-testid="promo-percent" type="number" value={campForm.percent} onChange={(e) => setCampForm({ ...campForm, percent: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Début</Label><Input data-testid="promo-start" type="date" value={campForm.start_date} onChange={(e) => setCampForm({ ...campForm, start_date: e.target.value })} /></div>
              <div><Label>Fin</Label><Input data-testid="promo-end" type="date" value={campForm.end_date} onChange={(e) => setCampForm({ ...campForm, end_date: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={campForm.active} onCheckedChange={(v) => setCampForm({ ...campForm, active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCampOpen(false)}>Annuler</Button><Button data-testid="save-promo-btn" onClick={saveCamp} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog code */}
      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{codeEdit ? "Modifier le code" : "Nouveau code"}</DialogTitle><DialogDescription>Code promo ou ambassadeur.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Code</Label><Input data-testid="code-code" value={codeForm.code} onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value.toUpperCase() })} placeholder="ex: INSTA20" /></div>
            <div><Label>Type</Label>
              <select data-testid="code-type" className={selCls} value={codeForm.type} onChange={(e) => setCodeForm({ ...codeForm, type: e.target.value })}>
                <option value="campaign">Code campagne (à partager)</option>
                <option value="ambassador">Ambassadeur (lié à un client)</option>
              </select>
            </div>
            {codeForm.type === "ambassador" && (
              <div><Label>Client ambassadeur</Label>
                <select data-testid="code-owner" className={selCls} value={codeForm.owner_customer_id} onChange={(e) => setCodeForm({ ...codeForm, owner_customer_id: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}
                </select>
              </div>
            )}
            <div><Label>Réduction (%)</Label><Input data-testid="code-percent" type="number" value={codeForm.percent} onChange={(e) => setCodeForm({ ...codeForm, percent: e.target.value })} /></div>
            <div><Label>Portée</Label>
              <select data-testid="code-scope" className={selCls} value={codeForm.scope} onChange={(e) => setCodeForm({ ...codeForm, scope: e.target.value })}>
                <option value="first_order">1ʳᵉ commande du client</option>
                <option value="all_orders">Toutes les commandes</option>
              </select>
            </div>
            <div className="flex items-center gap-2"><Switch checked={codeForm.active} onCheckedChange={(v) => setCodeForm({ ...codeForm, active: v })} /><Label>Actif</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCodeOpen(false)}>Annuler</Button><Button data-testid="save-code-btn" onClick={saveCode} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
